import React from 'react';
import moment from 'moment';
import { Lozenge } from '@packages/ui/primitives/Lozenge';
import { DATE_FORMAT } from '@packages/shared/constants';
import { CommaFormatted, convertNumberWithCommas } from '@packages/shared/utils';
import { ActionRegistry } from '../../registry/ActionRegistry';
import type { ActionIntentConfig, ConditionalDisplayRule } from '../schema/types';

const INTENT_LINK_STYLE: React.CSSProperties = {
  cursor: 'pointer',
  textDecoration: 'underline',
  color: 'var(--color-iris-500, #6366f1)',
};

const STYLE_CLASSES: Record<string, string> = {
  strikethrough: 'line-through text-muted-foreground',
  muted: 'opacity-50',
  bold: 'font-bold',
  italic: 'italic',
  highlight: 'bg-amber-50 dark:bg-amber-950 px-1 rounded',
};

function isEmptyValue(value: unknown): boolean {
  return value == null || value === '' || value === 0;
}

function evaluateRule(rule: Readonly<ConditionalDisplayRule>, row: Record<string, unknown>): boolean {
  const fieldValue = row[rule.when.field];
  switch (rule.when.operator) {
    case 'EXISTS':
      return fieldValue != null && fieldValue !== '' && fieldValue !== false;
    case 'NOT_EXISTS':
      return fieldValue == null || fieldValue === '' || fieldValue === false;
    case 'EQUAL':
      return fieldValue === rule.when.value;
    case 'NOT_EQUAL':
      return fieldValue !== rule.when.value;
    default:
      return false;
  }
}

function formatShowValue(value: unknown, columnType: string): string {
  if (value == null || value === '') return '-';
  switch (columnType) {
    case 'date':
    case 'conditionalDate': {
      const m = moment(value as string);
      return m.isValid() ? m.format(DATE_FORMAT) : String(value);
    }
    case 'dateTime':
    case 'dateTimeLTS': {
      const m = moment(value as string);
      return m.isValid() ? m.format(DATE_FORMAT) : String(value);
    }
    case 'currency':
      return `$${convertNumberWithCommas(value, true)}`;
    case 'number':
      return CommaFormatted(String(value)) ?? String(value);
    case 'percentage':
      return `${value}%`;
    default:
      return String(value);
  }
}

interface ActionIntentWrapperProps {
  config: ActionIntentConfig;
  row: Record<string, unknown>;
  value: unknown;
  children: React.ReactNode;
}

export function ActionIntentWrapper({ config, row, value, children }: ActionIntentWrapperProps) {
  if (config.whenHasValue !== false && isEmptyValue(value)) {
    return <>{children}</>;
  }

  const handleClick = () => {
    const payload: Record<string, unknown> = {};
    for (const [key, fieldName] of Object.entries(config.payload)) {
      payload[key] = row[fieldName];
    }
    ActionRegistry.emitIntent({
      type: 'OPEN_MODAL',
      actionId: config.actionId,
      payload,
    });
  };

  return (
    <span
      role='button'
      tabIndex={0}
      style={INTENT_LINK_STYLE}
      onClick={handleClick}
      onKeyDown={(e) => { if (e.key === 'Enter') handleClick(); }}
    >
      {children}
    </span>
  );
}

interface ConditionalDisplayWrapperProps {
  rules: ReadonlyArray<Readonly<ConditionalDisplayRule>>;
  row: Record<string, unknown>;
  baseContent: React.ReactNode;
  columnType: string;
}

export function ConditionalDisplayWrapper({ rules, row, baseContent, columnType }: ConditionalDisplayWrapperProps) {
  const matchedRule = rules.find((rule) => evaluateRule(rule, row));
  if (!matchedRule) return <>{baseContent}</>;

  const styleClass = matchedRule.style ? STYLE_CLASSES[matchedRule.style] : undefined;
  const hideOriginal = matchedRule.style === 'hidden';

  let showContent: React.ReactNode = null;
  if (matchedRule.show != null) {
    const isRowField = matchedRule.show in row;
    const displayText = isRowField
      ? formatShowValue(row[matchedRule.show], columnType)
      : matchedRule.show;

    const displayAs = matchedRule.as ?? 'badge';
    const variant = matchedRule.variant ?? 'default';

    switch (displayAs) {
      case 'badge':
        showContent = <Lozenge variant={variant as any}>{displayText}</Lozenge>;
        break;
      case 'tag':
        showContent = (
          <span className='text-xs px-1.5 py-0.5 rounded border border-current opacity-80'>
            {displayText}
          </span>
        );
        break;
      case 'inline':
        showContent = <span>{displayText}</span>;
        break;
      default: {
        const _exhaustive: never = displayAs;
        showContent = <span>{displayText}</span>;
      }
    }
  }

  return (
    <div className='flex items-center gap-2'>
      {!hideOriginal && (
        styleClass ? <span className={styleClass}>{baseContent}</span> : <>{baseContent}</>
      )}
      {showContent}
    </div>
  );
}
